import { isDefined, isNotDefined } from '@sgme/fp';
import { useSgwtWidgets } from '@sgwt/sgwt-widgets-react';
import deepEqual from 'fast-deep-equal';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useCurrentUserQuery } from '@/api/user/index.ts';
import { CommercialMarginPanel } from '@/App/tiles/shared/CommercialMargin/CommercialMarginPanel.tsx';
import { createForwardMarginConverter } from '@/App/tiles/shared/CommercialMargin/converters.ts';
import type { MarginSectionProps } from '@/App/tiles/shared/CommercialMargin/MarginSection.tsx';
import { selectCashTileMarginAmountCurrency } from '@/store/cash/selectors/common.ts';
import { selectCashPriceStatus } from '@/store/cash/selectors/prices.ts';
import {
  cashCommercialMarginCurrencyToggled,
  cashCommercialSpotMarginUpdated,
  cashCommercialSwapMarginUpdated,
} from '@/store/cash/slice.ts';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { roundToFixed } from '@/utils/number.ts';

export type ForwardCommercialMarginProps = {
  tileId: string;
  isCommercialMarginVisible: boolean;
  onCommercialMarginVisibilityToggle: () => void;
};

/**
 * ForwardCommercialMargin component for managing and displaying commercial margin spot and swap points.
 * Displays two sections: spot points and swap points with their respective amounts.
 * Only visible for Internal Sales users when RFS prices are available.
 */
export const ForwardCommercialMargin = (props: ForwardCommercialMarginProps) => {
  const { tileId, isCommercialMarginVisible, onCommercialMarginVisibilityToggle } = props;

  const { sgwtWebAnalytics } = useSgwtWidgets();
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();

  const { data: user } = useCurrentUserQuery();

  const priceStatus = useAppSelector((state) => selectCashPriceStatus(state, tileId), deepEqual);
  const marginAmountCurrency = useAppSelector((state) => selectCashTileMarginAmountCurrency(state, tileId));

  const handleUpdateSpotMargin = useCallback(
    (value: { bid: number; ask: number }) => {
      sgwtWebAnalytics?.trackEvent('Cash', 'Commercial margin spot points changed', `${value.bid}/${value.ask}`);

      dispatch(
        cashCommercialSpotMarginUpdated({
          tileId,
          bid: roundToFixed(value.bid, 1),
          ask: roundToFixed(value.ask, 1),
        }),
      );
    },
    [tileId, sgwtWebAnalytics],
  );

  const handleResetSpotMargin = useCallback(
    (defaultSpotPoints: { bid: number; ask: number }) => {
      sgwtWebAnalytics?.trackEvent(
        'Cash',
        'Commercial margin spot points reseted',
        `${defaultSpotPoints.bid}/${defaultSpotPoints.ask}`,
      );

      dispatch(
        cashCommercialSpotMarginUpdated({
          tileId,
          bid: defaultSpotPoints.bid,
          ask: defaultSpotPoints.ask,
        }),
      );
    },
    [tileId, sgwtWebAnalytics],
  );

  const handleUpdateSwapMargin = useCallback(
    (value: { bid: number; ask: number }) => {
      sgwtWebAnalytics?.trackEvent('Cash', 'Commercial margin swap pps changed', `${value.bid}/${value.ask}`);

      dispatch(
        cashCommercialSwapMarginUpdated({
          tileId,
          bid: roundToFixed(value.bid, 1),
          ask: roundToFixed(value.ask, 1),
        }),
      );
    },
    [tileId, sgwtWebAnalytics],
  );

  const handleResetSwapMargin = useCallback(
    (defaultSwapPoints: { bid: number; ask: number }) => {
      sgwtWebAnalytics?.trackEvent(
        'Cash',
        'Commercial margin swap points reseted',
        `${defaultSwapPoints.bid}/${defaultSwapPoints.ask}`,
      );

      dispatch(
        cashCommercialSwapMarginUpdated({
          tileId,
          bid: defaultSwapPoints.bid,
          ask: defaultSwapPoints.ask,
        }),
      );
    },
    [tileId, sgwtWebAnalytics],
  );

  const handleToggleCurrency = useCallback(() => {
    sgwtWebAnalytics?.trackEvent('Cash', 'Commercial margin currency toggled');
    dispatch(cashCommercialMarginCurrencyToggled({ tileId }));
  }, [tileId, sgwtWebAnalytics]);

  if (isNotDefined(user?.userType) || user.userType !== 'Internal Sales') {
    return null;
  }

  const isCommercialMarginEditable = isDefined(priceStatus) && priceStatus.status === 'rfs-price';

  const {
    commercialMarginSpotPoints = { bid: 0, ask: 0 },
    commercialMarginDefaultSpotPoints = { bid: 0, ask: 0 },
    commercialMarginSwapPoints = { bid: 0, ask: 0 },
    commercialMarginDefaultSwapPoints = { bid: 0, ask: 0 },
    commercialMarginAmount = { bid: 0, ask: 0 },
    commercialMarginAmountCurrency,
  } = priceStatus.status === 'rfs-price' ? priceStatus : {};

  const sections: MarginSectionProps[] = [
    {
      type: 'spot',
      label: formatMessage({ id: 'CashTile.commercialMargin.spotPps' }),
      value: commercialMarginSpotPoints,
      defaultValue: commercialMarginDefaultSpotPoints,
      increment: 0.1,
      isEditable: isCommercialMarginEditable,
      converter: createForwardMarginConverter(),
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
      dataE2ePrefix: 'commercial-margin-spot-pps',
      onUpdate: handleUpdateSpotMargin,
      onReset: commercialMarginDefaultSpotPoints
        ? () => handleResetSpotMargin(commercialMarginDefaultSpotPoints)
        : undefined,
    },
    {
      type: 'swap',
      label: formatMessage({ id: 'CashTile.commercialMargin.swapPps' }),
      value: commercialMarginSwapPoints,
      defaultValue: commercialMarginDefaultSwapPoints,
      increment: 0.1,
      isEditable: isCommercialMarginEditable,
      converter: createForwardMarginConverter(),
      minimumFractionDigits: 1,
      maximumFractionDigits: 2,
      dataE2ePrefix: 'commercial-margin-swap-pps',
      onUpdate: handleUpdateSwapMargin,
      onReset: commercialMarginDefaultSwapPoints
        ? () => handleResetSwapMargin(commercialMarginDefaultSwapPoints)
        : undefined,
    },
  ];

  return (
    <CommercialMarginPanel
      titleKey="cash.forward.commercialMargin.title"
      isOpened={isCommercialMarginVisible}
      isEditable={isCommercialMarginEditable}
      onToggle={onCommercialMarginVisibilityToggle}
      sections={sections}
      marginAmount={commercialMarginAmount}
      currency={commercialMarginAmountCurrency ?? marginAmountCurrency}
      onCurrencyToggle={handleToggleCurrency}
      data-e2e="commercial-margin-toggle-button"
    />
  );
};
